import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Turnstile } from '@marsidev/react-turnstile';
import { api } from '../services/api';
import { COPY } from '../lib/copyDictionary';
import { useGamenics, GamenicsEvent } from '../hooks/useGamenics';
import { Icon } from './icons/Icon';

interface LoginModalProps {
    onClose: () => void;
    onLoginSuccess: (user: any) => void;
}

export const LoginModal: React.FC<LoginModalProps> = ({ onClose, onLoginSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
    const { playSound } = useGamenics();
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const siteKey = isLocal ? "1x00000000000000000000AA" : "0x4AAAAAAB_lyh-P4GIy4FGV";

    const handleDebugLogin = () => {
        playSound(GamenicsEvent.TAP_CONFIRM);
        window.location.href = '/api/auth/debug-login?userId=user_1';
    };

    const handleGoogleLogin = async () => {
        setLoading(true);
        setError(null);
        if (!turnstileToken) {
            setError('セキュリティチェックを完了してください。');
            setLoading(false);
            return;
        }
        const result = await api.login(turnstileToken);
        if (result.success) {
            onLoginSuccess(result.user);
            onClose();
        } else {
            setError(result.error || 'ログインに失敗しました');
            setLoading(false);
        }
    };

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                <motion.div
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    className="absolute inset-0 bg-ink/70 backdrop-blur-sm"
                    onClick={onClose}
                />
                <motion.div
                    initial={{ scale: 0.92, opacity: 0, y: 10 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.92, opacity: 0 }}
                    className="relative bg-surface border-2 border-ink rounded-pop shadow-hard p-8 w-full max-w-sm"
                >
                    <button
                        onClick={() => { playSound(GamenicsEvent.TAP_CANCEL); onClose(); }}
                        aria-label={COPY.close}
                        className="absolute top-4 right-4 w-9 h-9 rounded-full bg-cream-2 border-2 border-ink hover:bg-sun flex items-center justify-center text-ink"
                    >
                        <Icon name="x" size={16} />
                    </button>

                    <h2 className="text-2xl font-display font-black text-center mb-2 text-ink flex items-center justify-center gap-2">
                        ログイン / 登録
                        {isLocal && (
                            <span className="flex items-center gap-1">
                                <span className="w-2 h-2 bg-role-voice rounded-full animate-pulse" />
                                <span className="text-[10px] text-role-voice font-mono">[LOCAL]</span>
                            </span>
                        )}
                    </h2>
                    <p className="text-ink-2 text-center text-sm mb-7">
                        アカウントを作成して、表現を届けよう。
                    </p>

                    <div className="flex flex-col gap-4">
                        {isLocal && (
                            <button onClick={handleDebugLogin} className="btn btn-accent w-full justify-center py-3.5">
                                デバッグログイン (公式)
                            </button>
                        )}

                        <button
                            onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); handleGoogleLogin(); }}
                            disabled={loading}
                            className="btn btn-ghost w-full justify-center py-3.5 disabled:opacity-60"
                        >
                            {loading ? (
                                <div className="w-5 h-5 border-2 border-ink border-t-transparent rounded-full animate-spin" />
                            ) : (
                                <>
                                    <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
                                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
                                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                    </svg>
                                    {COPY.login}
                                </>
                            )}
                        </button>

                        <div className="relative my-1">
                            <div className="absolute inset-0 flex items-center"><span className="w-full border-t-2 border-cream-2" /></div>
                            <div className="relative flex justify-center text-xs uppercase tracking-widest">
                                <span className="bg-surface px-2 text-ink-3 font-bold">Security</span>
                            </div>
                        </div>

                        <div className="flex justify-center min-h-[65px]">
                            <Turnstile
                                key={isLocal ? "dev" : "prod"}
                                siteKey={siteKey}
                                onSuccess={(token) => setTurnstileToken(token)}
                                onError={() => setError('セキュリティチェックに失敗しました。再度お試しください。')}
                                options={{ theme: 'light', size: 'flexible' }}
                            />
                        </div>

                        {error && (
                            <p className="text-red-600 text-sm text-center bg-[#fdecec] border-2 border-red-300 p-2 rounded-pop-sm font-bold">{error}</p>
                        )}
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
};
